import { useEffect, useState } from 'react';
import { Card, Button, Input, Select, EmptyState } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { US_STATES } from '../utils/usStates';
import { useProviderCredentials } from '../hooks/useProviderCredentials';
import type { ProviderCredentialRow } from '../api/setupApi';
import { AddProviderRow } from './AddProviderRow';
import styles from './ProviderCredentialsTable.module.css';

function ProviderRow({ provider }: { provider: ProviderCredentialRow }) {
  const { saveProvider } = useProviderCredentials();
  const [individualNpi, setIndividualNpi] = useState(provider.individualNpi ?? '');
  const [licenseNumber, setLicenseNumber] = useState(provider.licenseNumber ?? '');
  const [licenseState, setLicenseState] = useState(provider.licenseState ?? '');

  useEffect(() => {
    setIndividualNpi(provider.individualNpi ?? '');
    setLicenseNumber(provider.licenseNumber ?? '');
    setLicenseState(provider.licenseState ?? '');
  }, [provider]);

  return (
    <div className={`${patterns.gridTableRow} ${styles.gridCols5}`}>
      <div className={patterns.cellPrimary}>{provider.name}</div>
      <Input
        className={styles.mono}
        value={individualNpi}
        onChange={(e) => setIndividualNpi(e.target.value)}
      />
      <Input
        className={styles.mono}
        value={licenseNumber}
        onChange={(e) => setLicenseNumber(e.target.value)}
      />
      <Select
        options={US_STATES}
        placeholder="State"
        value={licenseState}
        onChange={(e) => setLicenseState(e.target.value)}
      />
      <div className={styles.actionCell}>
        <Button
          size="sm"
          variant="secondary"
          isLoading={saveProvider.isPending}
          onClick={() =>
            saveProvider.mutate({
              staffMemberId: provider.staffMemberId,
              individualNpi,
              licenseNumber: licenseNumber || undefined,
              licenseState: licenseState || undefined,
            })
          }
        >
          Save
        </Button>
      </div>
    </div>
  );
}

export function ProviderCredentialsTable() {
  const { providers, isLoading } = useProviderCredentials();
  const [showAddRow, setShowAddRow] = useState(false);

  return (
    <Card
      title="Provider credentials — from your staff roster"
      action={
        <Button size="sm" variant="secondary" onClick={() => setShowAddRow(true)}>
          + Add provider
        </Button>
      }
    >
      {!isLoading && providers.length === 0 && !showAddRow ? (
        <EmptyState
          compact
          title="No providers yet"
          description="Add a provider to capture their NPI and license for insurance verification calls."
        />
      ) : (
        <div className={patterns.gridTable}>
          <div className={`${patterns.gridTableHeader} ${styles.gridCols5}`}>
            <span>Provider</span>
            <span>NPI (Type 1)</span>
            <span>State license</span>
            <span>State</span>
            <span />
          </div>
          {providers.map((p) => (
            <ProviderRow key={p.staffMemberId} provider={p} />
          ))}
          {showAddRow && <AddProviderRow onDone={() => setShowAddRow(false)} />}
        </div>
      )}
    </Card>
  );
}
